import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react"
import { BulkVideoProgressDto } from "@/modules/recruitment/applications/applicants-service"

export interface BulkVideoJob {
  jobId: string
  total: number
  processed: number
  status: "Pending" | "InProgress" | "Completed" | "Failed"
}

interface BulkVideoContextValue {
  jobs: BulkVideoJob[]
  /** Count of jobs that are still Pending or InProgress */
  activeCount: number
  addJob: (job: BulkVideoJob) => void
  updateJob: (progress: BulkVideoProgressDto) => void
  clearCompleted: () => void
}

const BulkVideoContext = createContext<BulkVideoContextValue | null>(null)

export function BulkVideoProvider({ children }: { children: ReactNode }) {
  const [jobs, setJobs] = useState<BulkVideoJob[]>([])

  const addJob = useCallback((job: BulkVideoJob) => {
    setJobs((prev) => [...prev, job])
  }, [])

  const updateJob = useCallback((progress: BulkVideoProgressDto) => {
    setJobs((prev) =>
      prev.map((j) => (j.jobId === progress.jobId ? { ...j, ...progress } : j)),
    )
  }, [])

  const clearCompleted = useCallback(() => {
    setJobs((prev) =>
      prev.filter((j) => j.status !== "Completed" && j.status !== "Failed"),
    )
  }, [])

  const activeCount = jobs.filter(
    (j) => j.status === "Pending" || j.status === "InProgress",
  ).length

  return (
    <BulkVideoContext.Provider
      value={{ jobs, activeCount, addJob, updateJob, clearCompleted }}
    >
      {children}
    </BulkVideoContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useBulkVideoContext(): BulkVideoContextValue {
  const ctx = useContext(BulkVideoContext)
  if (!ctx)
    throw new Error(
      "useBulkVideoContext must be used inside <BulkVideoProvider>",
    )
  return ctx
}
